import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { filter, finalize } from 'rxjs';
import { CurrentContextService } from '../../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  growthSearchOpsTopic,
  liveStatusTranslationKey
} from '../../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../../core/live-updates/live-update.service';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinDatePipe } from '../../../shared/design-system/ain-date.pipe';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { IdentityUserSelectorComponent } from '../../../shared/design-system/identity-user-selector.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../../shared/design-system/page.component';
import { AinStateComponent } from '../../../shared/design-system/state.component';
import { StructuredJsonDisplayComponent } from '../../../shared/design-system/structured-json-display.component';
import { StructuredJsonFormComponent } from '../../../shared/design-system/structured-json-form.component';
import {
  SEARCH_OPS_CMS_TYPES,
  SEARCH_OPS_GOAL_METRICS,
  SEARCH_OPS_LOCALES,
  SEARCH_OPS_SITE_KINDS,
  SEARCH_OPS_STATUSES,
  SearchPositionCatalogItemDto,
  SearchPropertyDetailDto,
  SearchTeamSeatDto,
  SearchOpsWebhookDeliveryListItem,
  searchOpsExternalAdapterFields,
  searchOpsOnboardingFields,
  searchOpsStorefrontBindingFields
} from './growth-search-ops.models';
import { GrowthSearchOpsService } from './growth-search-ops.service';
import { GrowthSearchOpsStorefrontBindingComponent } from './growth-search-ops-storefront-binding.component';

@Component({
  standalone: true,
  selector: 'ain-growth-search-ops-property-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    AinFormFieldComponent,
    AinDatePipe,
    StructuredJsonFormComponent,
    StructuredJsonDisplayComponent,
    GrowthSearchOpsStorefrontBindingComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    MultiSelectModule,
    CheckboxModule,
    DialogModule,
    TabsModule,
    RouterLink,
    IdentityUserSelectorComponent
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="detail()?.name || t('growth.searchops.propertyDetail.title')"
      [description]="t('growth.searchops.propertyDetail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.searchops.propertyDetail.help.title'"
        [descriptionKey]="'growth.searchops.propertyDetail.help.description'"
        [bulletKeys]="[
          'growth.searchops.propertyDetail.help.bullets.team',
          'growth.searchops.propertyDetail.help.bullets.goals',
          'growth.searchops.propertyDetail.help.bullets.adapter'
        ]" />

      <div class="toolbar">
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/properties']" [queryParams]="backParams" [label]="t('common.back')"></a>
        <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.error')" [message]="error()" />
      } @else if (!detail()) {
        <ain-state type="empty" [title]="t('growth.searchops.propertyDetail.notFound')" />
      } @else {
        <section class="card header-card">
          <div class="summary">
            <strong>{{ detail()!.primaryDomain }}</strong>
            <span>{{ t('growth.searchops.siteKind.' + detail()!.siteKind) }}</span>
            <span>{{ t('growth.searchops.status.' + detail()!.status) }}</span>
          </div>
        </section>

        <p-tabs value="overview">
          <p-tablist>
            <p-tab value="overview">{{ t('growth.searchops.propertyDetail.tabs.overview') }}</p-tab>
            <p-tab value="team">{{ t('growth.searchops.propertyDetail.tabs.team') }}</p-tab>
            <p-tab value="goals">{{ t('growth.searchops.propertyDetail.tabs.goals') }}</p-tab>
            <p-tab value="adapter">{{ t('growth.searchops.propertyDetail.tabs.adapter') }}</p-tab>
          </p-tablist>
          <p-tabpanels>
            <p-tabpanel value="overview">
              <section class="card">
                <ain-info-hint
                  [titleKey]="'growth.searchops.onboard.help.title'"
                  [descriptionKey]="'growth.searchops.onboard.help.description'" />
                <div class="dialog-grid">
                  <ain-form-field [label]="t('growth.searchops.fields.name')">
                    <input pInputText [(ngModel)]="editModel.name" />
                  </ain-form-field>
                  <ain-form-field [label]="t('growth.searchops.fields.primaryDomain')">
                    <input pInputText [(ngModel)]="editModel.primaryDomain" />
                  </ain-form-field>
                  <ain-form-field [label]="t('growth.searchops.fields.siteKind')">
                    <p-select [options]="siteKindOptions()" optionLabel="label" optionValue="value" [(ngModel)]="editModel.siteKind" appendTo="body" (ngModelChange)="onSiteKindChange($event)" />
                  </ain-form-field>
                  @if (editModel.siteKind !== 'ain_storefront') {
                    <ain-form-field [label]="t('growth.searchops.fields.cmsType')">
                      <p-select [options]="cmsOptions()" optionLabel="label" optionValue="value" [(ngModel)]="editModel.cmsType" appendTo="body" />
                    </ain-form-field>
                  }
                  <ain-form-field [label]="t('growth.searchops.fields.status')">
                    <p-select [options]="statusOptions()" optionLabel="label" optionValue="value" [(ngModel)]="editModel.status" appendTo="body" />
                  </ain-form-field>
                  <ain-form-field [label]="t('growth.searchops.fields.locales')">
                    <p-multiSelect [options]="localeOptions()" optionLabel="label" optionValue="value" [(ngModel)]="editLocales" appendTo="body" />
                  </ain-form-field>
                  @if (editModel.siteKind === 'ain_storefront') {
                    <div class="full">
                      <ain-growth-search-ops-storefront-binding [(payloadJson)]="editModel.storefrontBindingJson" />
                    </div>
                  }
                  <div class="full">
                    <ain-form-field [label]="t('growth.searchops.fields.onboarding')">
                      <ain-structured-json-form [fields]="onboardingFields" [(payloadJson)]="editModel.onboardingJson" />
                    </ain-form-field>
                  </div>
                </div>
                <button pButton type="button" [label]="t('common.save')" [loading]="saving()" (click)="saveProperty()"></button>
              </section>
            </p-tabpanel>
            <p-tabpanel value="team">
              <section class="card">
                <ain-info-hint
                  [titleKey]="'growth.searchops.team.help.title'"
                  [descriptionKey]="'growth.searchops.team.help.description'" />
                <div class="toolbar">
                  <button pButton type="button" [label]="t('growth.searchops.team.actions.add')" (click)="openSeat()"></button>
                </div>
                <p-table [value]="detail()!.seats" [paginator]="true" [rows]="10">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('growth.searchops.fields.user') }}</th>
                      <th>{{ t('growth.searchops.fields.capacity') }}</th>
                      <th>{{ t('growth.searchops.fields.positions') }}</th>
                      <th>{{ t('growth.searchops.fields.active') }}</th>
                      <th></th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td><ain-identity-user-selector [ngModel]="row.userId" [disabled]="true" /></td>
                      <td>{{ row.weeklyHourCapacity }}</td>
                      <td>{{ positionLabels(row) }}</td>
                      <td>{{ row.active ? t('common.yes') : t('common.no') }}</td>
                      <td><button pButton size="small" severity="secondary" [label]="t('common.edit')" (click)="openSeat(row)"></button></td>
                    </tr>
                  </ng-template>
                </p-table>
              </section>
            </p-tabpanel>
            <p-tabpanel value="goals">
              <section class="card">
                <ain-info-hint
                  [titleKey]="'growth.searchops.goals.help.title'"
                  [descriptionKey]="'growth.searchops.goals.help.description'" />
                <div class="toolbar">
                  <button pButton type="button" [label]="t('growth.searchops.goals.actions.add')" (click)="openGoal()"></button>
                </div>
                <p-table [value]="detail()!.goals" [paginator]="true" [rows]="10">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('growth.searchops.fields.metric') }}</th>
                      <th>{{ t('growth.searchops.fields.baseline') }}</th>
                      <th>{{ t('growth.searchops.fields.target') }}</th>
                      <th>{{ t('growth.searchops.fields.unit') }}</th>
                      <th>{{ t('growth.searchops.fields.ownerPosition') }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td>{{ t('growth.searchops.metrics.' + row.metricCode) }}</td>
                      <td>{{ row.baseline }}</td>
                      <td>{{ row.target }}</td>
                      <td>{{ row.unit }}</td>
                      <td>{{ row.ownerPositionCode ? t('growth.searchops.positions.' + row.ownerPositionCode) : '—' }}</td>
                    </tr>
                  </ng-template>
                </p-table>
              </section>
            </p-tabpanel>
            <p-tabpanel value="adapter">
              <section class="card">
                <ain-info-hint
                  [titleKey]="'growth.searchops.adapter.help.title'"
                  [descriptionKey]="'growth.searchops.adapter.help.description'"
                  [bulletKeys]="[
                    'growth.searchops.adapter.help.bullets.status',
                    'growth.searchops.adapter.help.bullets.endpoint',
                    'growth.searchops.adapter.help.bullets.deliveries'
                  ]" />
                <p>{{ t('growth.searchops.fields.adapterKey') }}: {{ t('growth.searchops.adapterKey.' + detail()!.adapter.adapterKey) }}</p>
                <p>{{ t('growth.searchops.fields.adapterStatus') }}: {{ t('growth.searchops.adapterStatus.' + detail()!.adapter.status) }}</p>
                @if (detail()!.siteKind === 'ain_storefront') {
                  <ain-structured-json-display [fields]="storefrontFields" [payloadJson]="detail()!.adapter.configJson" />
                } @else {
                  <ain-structured-json-display [fields]="externalAdapterFields" [payloadJson]="detail()!.adapter.configJson" />
                  @if (canManageConnector()) {
                    <div class="dialog-grid single">
                      <ain-form-field [label]="t('growth.searchops.fields.endpointUrl')">
                        <input pInputText [(ngModel)]="externalEndpointUrl" />
                      </ain-form-field>
                      <ain-form-field [label]="t('growth.searchops.fields.ackTimeoutMinutes')">
                        <p-inputNumber [(ngModel)]="ackTimeoutMinutes" [min]="1" [max]="10080" appendTo="body" />
                      </ain-form-field>
                      <label class="checkbox-row">
                        <p-checkbox [(ngModel)]="rotateApiKey" [binary]="true" />
                        <span>{{ t('growth.searchops.adapter.rotateApiKey') }}</span>
                      </label>
                    </div>
                    <button pButton type="button" [label]="t('growth.searchops.adapter.actions.saveEndpoint')" [loading]="saving()" (click)="saveExternalAdapter()"></button>
                  }
                  @if (revealedApiKey()) {
                    <p class="secret-banner">{{ t('growth.searchops.adapter.apiKeyOnce') }}: <code>{{ revealedApiKey() }}</code></p>
                  }
                  @if (revealedSigningSecret()) {
                    <p class="secret-banner">{{ t('growth.searchops.adapter.signingSecretOnce') }}: <code>{{ revealedSigningSecret() }}</code></p>
                  }
                  <div class="section-title">
                    <h3>{{ t('growth.searchops.adapter.deliveries.title') }}</h3>
                    @if (eventHubSubscriptionId()) {
                      <a pButton severity="secondary" [routerLink]="['/event-hub/webhook-subscriptions', eventHubSubscriptionId()]" [label]="t('growth.searchops.adapter.deliveries.openHub')"></a>
                    }
                  </div>
                  @if (!deliveries().length) {
                    <ain-state type="empty" [title]="t('growth.searchops.adapter.deliveries.empty')" />
                  } @else {
                    <p-table [value]="deliveries()" [paginator]="true" [rows]="10">
                      <ng-template pTemplate="header">
                        <tr>
                          <th>{{ t('growth.searchops.adapter.deliveries.eventType') }}</th>
                          <th>{{ t('growth.searchops.adapter.deliveries.attempt') }}</th>
                          <th>{{ t('growth.searchops.adapter.deliveries.status') }}</th>
                          <th>{{ t('growth.searchops.adapter.deliveries.deliveredAt') }}</th>
                        </tr>
                      </ng-template>
                      <ng-template pTemplate="body" let-row>
                        <tr>
                          <td class="wrap-url">{{ row.eventType }}</td>
                          <td>{{ row.attempt }}</td>
                          <td>{{ row.isSuccess ? t('growth.searchops.adapter.deliveries.success') : t('growth.searchops.adapter.deliveries.failed') }}</td>
                          <td>{{ row.deliveredAtUtc | ainDate:'datetime' }}</td>
                        </tr>
                      </ng-template>
                    </p-table>
                  }
                }
              </section>
            </p-tabpanel>
          </p-tabpanels>
        </p-tabs>
      }
    </ain-page>

    <p-dialog [header]="t('growth.searchops.team.dialogTitle')" [(visible)]="seatVisible" [modal]="true" [style]="{ width: '32rem' }" appendTo="body">
      <div class="dialog-grid single">
        <ain-form-field [label]="t('growth.searchops.fields.user')">
          <ain-identity-user-selector [(ngModel)]="seatModel.userId" [disabled]="!!seatModel.id" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.searchops.fields.capacity')">
          <p-inputNumber [(ngModel)]="seatModel.weeklyHourCapacity" [min]="0" [max]="168" appendTo="body" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.searchops.fields.positions')">
          <p-multiSelect [options]="positionOptions()" optionLabel="label" optionValue="value" [(ngModel)]="seatModel.positionCodes" appendTo="body" />
        </ain-form-field>
      </div>
      <ng-template pTemplate="footer">
        <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="seatVisible = false"></button>
        <button pButton type="button" [label]="t('common.save')" [loading]="saving()" (click)="saveSeat()"></button>
      </ng-template>
    </p-dialog>

    <p-dialog [header]="t('growth.searchops.goals.dialogTitle')" [(visible)]="goalVisible" [modal]="true" [style]="{ width: '32rem' }" appendTo="body">
      <div class="dialog-grid single">
        <ain-form-field [label]="t('growth.searchops.fields.metric')">
          <p-select [options]="metricOptions()" optionLabel="label" optionValue="value" [(ngModel)]="goalModel.metricCode" appendTo="body" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.searchops.fields.baseline')">
          <p-inputNumber [(ngModel)]="goalModel.baseline" appendTo="body" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.searchops.fields.target')">
          <p-inputNumber [(ngModel)]="goalModel.target" appendTo="body" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.searchops.fields.unit')">
          <input pInputText [(ngModel)]="goalModel.unit" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.searchops.fields.ownerPosition')">
          <p-select [options]="positionOptions()" optionLabel="label" optionValue="value" [(ngModel)]="goalModel.ownerPositionCode" [showClear]="true" appendTo="body" />
        </ain-form-field>
      </div>
      <ng-template pTemplate="footer">
        <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="goalVisible = false"></button>
        <button pButton type="button" [label]="t('common.save')" [loading]="saving()" (click)="saveGoal()"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; align-items: center; margin-bottom: 12px; }
    .card { background: var(--ain-color-surface, #fff); padding: 16px; border-radius: 12px; margin-bottom: 12px; }
    .summary { display: flex; gap: 12px; flex-wrap: wrap; }
    .dialog-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin: 10px 0; }
    .dialog-grid.single, .dialog-grid .full { grid-template-columns: 1fr; }
    .dialog-grid .full { grid-column: 1 / -1; }
    .live-badge { font-size: 0.85rem; color: #64748b; }
    .checkbox-row { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
    .secret-banner { margin-top: 12px; word-break: break-word; }
    .wrap-url { overflow-wrap: anywhere; }
    .section-title { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin: 16px 0 8px; }
  `]
})
export class GrowthSearchOpsPropertyDetailComponent implements OnInit {
  private readonly api = inject(GrowthSearchOpsService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusKey = liveStatusTranslationKey;
  readonly storefrontFields = searchOpsStorefrontBindingFields;
  readonly externalAdapterFields = searchOpsExternalAdapterFields;
  readonly onboardingFields = searchOpsOnboardingFields;

  readonly detail = signal<SearchPropertyDetailDto | null>(null);
  readonly positions = signal<SearchPositionCatalogItemDto[]>([]);
  readonly deliveries = signal<SearchOpsWebhookDeliveryListItem[]>([]);
  readonly revealedApiKey = signal('');
  readonly revealedSigningSecret = signal('');
  readonly eventHubSubscriptionId = signal('');
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly error = signal('');
  backParams: Record<string, string> = {};
  editLocales: string[] = ['fa-IR'];
  editModel = {
    name: '',
    primaryDomain: '',
    siteKind: 'external',
    cmsType: 'wordpress',
    status: 'draft',
    storefrontBindingJson: '{}',
    onboardingJson: '{}'
  };
  externalEndpointUrl = '';
  ackTimeoutMinutes = 60;
  rotateApiKey = false;
  seatVisible = false;
  goalVisible = false;
  seatModel = { id: '', userId: '', weeklyHourCapacity: 8, active: true, positionCodes: [] as string[] };
  goalModel = { metricCode: 'organic_sessions', baseline: 0, target: 0, unit: 'sessions', ownerPositionCode: null as string | null };
  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.backParams = {
      ...(this.route.snapshot.queryParamMap.get('q') ? { q: this.route.snapshot.queryParamMap.get('q')! } : {}),
      ...(this.route.snapshot.queryParamMap.get('siteKind') ? { siteKind: this.route.snapshot.queryParamMap.get('siteKind')! } : {}),
      ...(this.route.snapshot.queryParamMap.get('first') ? { first: this.route.snapshot.queryParamMap.get('first')! } : {})
    };
    this.liveUpdateService.events$
      .pipe(filter(e => e.eventType === LiveUpdateEventTypes.growthSearchOpsChanged), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load(false));
    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.load(true));
    this.api.listPositions().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(items => this.positions.set(items));
    void this.setupLiveSubscription();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  siteKindOptions(): Array<{ label: string; value: string }> {
    return SEARCH_OPS_SITE_KINDS.map(value => ({ value, label: this.t(`growth.searchops.siteKind.${value}`) }));
  }

  cmsOptions(): Array<{ label: string; value: string }> {
    return SEARCH_OPS_CMS_TYPES.map(value => ({ value, label: this.t(`growth.searchops.cmsType.${value}`) }));
  }

  statusOptions(): Array<{ label: string; value: string }> {
    return SEARCH_OPS_STATUSES.map(value => ({ value, label: this.t(`growth.searchops.status.${value}`) }));
  }

  localeOptions(): Array<{ label: string; value: string }> {
    return SEARCH_OPS_LOCALES.map(value => ({ value, label: value }));
  }

  metricOptions(): Array<{ label: string; value: string }> {
    return SEARCH_OPS_GOAL_METRICS.map(value => ({ value, label: this.t(`growth.searchops.metrics.${value}`) }));
  }

  positionOptions(): Array<{ label: string; value: string }> {
    return this.positions().map(item => ({ value: item.code, label: this.t(`growth.searchops.positions.${item.code}`) }));
  }

  canManageConnector(): boolean {
    const ctx = this.context.context();
    if (ctx?.roles?.includes('SystemAdmin') || ctx?.isPlatformAdmin) {
      return true;
    }
    const keys = ctx?.permissions ?? [];
    return keys.some(item => item.key === 'growth.integration' || item.key === 'connectors.manage');
  }

  saveExternalAdapter(): void {
    const current = this.detail();
    if (!current || !this.externalEndpointUrl.trim()) return;
    this.saving.set(true);
    this.api.configureExternalAdapter(current.id, {
      endpointUrl: this.externalEndpointUrl.trim(),
      ackTimeoutMinutes: this.ackTimeoutMinutes,
      rotateApiKey: this.rotateApiKey
    }).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.saving.set(false))).subscribe({
      next: item => {
        this.detail.set({ ...current, adapter: item.adapter });
        this.eventHubSubscriptionId.set(item.eventHubSubscriptionId);
        this.revealedApiKey.set(item.apiKey ?? '');
        this.revealedSigningSecret.set(item.webhookSigningSecret ?? '');
        this.rotateApiKey = false;
        this.loadDeliveries(current.id);
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  positionLabels(row: SearchTeamSeatDto): string {
    return row.positions.map(item => this.t(`growth.searchops.positions.${item.positionCode}`)).join(', ');
  }

  openSeat(row?: SearchTeamSeatDto): void {
    this.seatModel = row
      ? { id: row.id, userId: row.userId, weeklyHourCapacity: row.weeklyHourCapacity, active: row.active, positionCodes: row.positions.map(item => item.positionCode) }
      : { id: '', userId: '', weeklyHourCapacity: 8, active: true, positionCodes: [] };
    this.seatVisible = true;
  }

  openGoal(): void {
    this.goalModel = { metricCode: 'organic_sessions', baseline: 0, target: 0, unit: 'sessions', ownerPositionCode: null };
    this.goalVisible = true;
  }

  onSiteKindChange(siteKind: string): void {
    if (siteKind === 'ain_storefront') {
      this.editModel.cmsType = 'ain';
      return;
    }
    this.editModel.storefrontBindingJson = '{}';
  }

  saveProperty(): void {
    const current = this.detail();
    if (!current) return;
    this.saving.set(true);
    this.api.updateProperty(current.id, {
      ...this.editModel,
      localesJson: JSON.stringify(this.editLocales ?? []),
      cmsType: this.editModel.siteKind === 'ain_storefront' ? 'ain' : this.editModel.cmsType,
      storefrontBindingJson: this.editModel.siteKind === 'ain_storefront' ? this.editModel.storefrontBindingJson : '{}'
    }).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.saving.set(false))).subscribe({
      next: item => this.applyDetail(item),
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  saveSeat(): void {
    const current = this.detail();
    if (!current || !this.seatModel.userId) return;
    this.saving.set(true);
    const request = {
      userId: this.seatModel.userId,
      weeklyHourCapacity: this.seatModel.weeklyHourCapacity,
      active: this.seatModel.active,
      positionCodes: this.seatModel.positionCodes
    };
    const call = this.seatModel.id
      ? this.api.updateSeat(current.id, this.seatModel.id, request)
      : this.api.createSeat(current.id, request);
    call.pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.saving.set(false))).subscribe({
      next: () => {
        this.seatVisible = false;
        this.load(false);
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  saveGoal(): void {
    const current = this.detail();
    if (!current) return;
    this.saving.set(true);
    this.api.createGoal(current.id, this.goalModel).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.saving.set(false))
    ).subscribe({
      next: () => {
        this.goalVisible = false;
        this.load(false);
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  load(showSpinner: boolean): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) return;
    if (showSpinner) this.loading.set(true);
    this.error.set('');
    this.api.getProperty(id).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.loading.set(false))).subscribe({
      next: item => {
        this.applyDetail(item);
        if (item.siteKind === 'external') {
          this.loadDeliveries(item.id);
        }
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  private applyDetail(item: SearchPropertyDetailDto): void {
    this.detail.set(item);
    this.editModel = {
      name: item.name,
      primaryDomain: item.primaryDomain,
      siteKind: item.siteKind,
      cmsType: item.cmsType,
      status: item.status,
      storefrontBindingJson: item.storefrontBindingJson,
      onboardingJson: item.onboardingJson
    };
    try {
      const parsed = JSON.parse(item.localesJson) as unknown;
      this.editLocales = Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : ['fa-IR'];
    } catch {
      this.editLocales = ['fa-IR'];
    }
    this.applyExternalAdapterForm(item.adapter.configJson);
  }

  private applyExternalAdapterForm(configJson: string): void {
    try {
      const parsed = JSON.parse(configJson || '{}') as Record<string, unknown>;
      this.externalEndpointUrl = typeof parsed['endpointUrl'] === 'string' ? parsed['endpointUrl'] : '';
      this.ackTimeoutMinutes = typeof parsed['ackTimeoutMinutes'] === 'number' ? parsed['ackTimeoutMinutes'] : 60;
      this.eventHubSubscriptionId.set(typeof parsed['eventHubSubscriptionId'] === 'string' ? parsed['eventHubSubscriptionId'] : '');
    } catch {
      this.externalEndpointUrl = '';
      this.ackTimeoutMinutes = 60;
      this.eventHubSubscriptionId.set('');
    }
  }

  private loadDeliveries(propertyId: string): void {
    this.api.listWebhookDeliveries(propertyId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: items => this.deliveries.set(items),
      error: () => this.deliveries.set([])
    });
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) return;
    this.subscribedTopic = growthSearchOpsTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
